import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { SmithChart, SmithPoint } from "@/components/visualizer/smith/SmithChart";
import { Button, KIND, SIZE } from "@/components/base-ui/Button";
import { Input } from "@/components/base-ui/Input";
import { Card } from "@/components/base-ui/Card";

export default function SmithChartPage() {
  const [z0, setZ0] = useState(50);
  const [point, setPoint] = useState<SmithPoint | null>(null);
  const [inputR, setInputR] = useState("");
  const [inputX, setInputX] = useState("");
  const [manualPoint, setManualPoint] = useState<{ r: number; x: number } | undefined>(undefined);

  const handlePointChange = useCallback((newPoint: SmithPoint) => {
    setPoint(newPoint);
    setInputR(newPoint.resistance.toFixed(2));
    setInputX(newPoint.reactance.toFixed(2));
  }, []);

  const handleImpedanceSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const r = parseFloat(inputR) || 0;
    const x = parseFloat(inputX) || 0;
    // Convert to normalized impedance
    setManualPoint({ r: r / z0, x: x / z0 });
  };

  const handleZ0Change = (newZ0: number) => {
    setZ0(newZ0);
    // Recalculate displayed values if we have a point
    if (point) {
      setInputR((point.r * newZ0).toFixed(2));
      setInputX((point.x * newZ0).toFixed(2));
    }
  };

  const formatComplex = (real: number, imag: number): string => {
    if (imag >= 0) {
      return `${real.toFixed(3)} + j${imag.toFixed(3)}`;
    }
    return `${real.toFixed(3)} - j${Math.abs(imag).toFixed(3)}`;
  };

  const formatImpedance = (r: number, x: number): string => {
    if (x >= 0) {
      return `${r.toFixed(2)} + j${x.toFixed(2)} Ω`;
    }
    return `${r.toFixed(2)} - j${Math.abs(x).toFixed(2)} Ω`;
  };

  return (
    <div className="container mx-auto qa pb-12">
      <div className="prose max-w-none mt-2 sm:mt-6 mb-8">
        <h3 className="text-3xl font-light tracking-wide text-gray-800 border-b border-gray-200 pb-2">Interactive Smith Chart</h3>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_350px] gap-8 items-start">
        {/* Chart Area */}
        <motion.div
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.5 }}
          className="flex flex-col"
        >
          <div className="w-full aspect-square bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <SmithChart
              z0={z0}
              onPointChange={handlePointChange}
              initialPoint={manualPoint}
            />
          </div>
          <p className="text-sm text-gray-500 mt-3 italic text-center xl:text-left w-full">
            Click or drag on the chart to select a complex impedance point.
          </p>
        </motion.div>

        {/* Controls and Display */}
        <motion.div
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-6 sticky top-4"
        >
          {/* Reference Impedance */}
          <Card>
            <div className="p-1">
              <label className="block text-sm font-serif font-medium text-gray-700 mb-3">
                Reference Impedance (Z₀)
              </label>
              <div className="flex gap-2">
                {[50, 75, 100].map((val) => (
                  <Button
                    key={val}
                    onClick={() => handleZ0Change(val)}
                    kind={z0 === val ? KIND.primary : KIND.secondary}
                    size={SIZE.compact}
                    overrides={{
                      BaseButton: {
                        style: {
                          width: "33%"
                        }
                      }
                    }}
                  >
                    {val}Ω
                  </Button>
                ))}
              </div>
            </div>
          </Card>

          {/* Manual Input */}
          <form onSubmit={handleImpedanceSubmit}>
            <Card>
              <div className="p-1">
                <label className="block text-sm font-serif font-medium text-gray-700 mb-4">
                  Input Impedance (Z = R + jX)
                </label>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 font-serif">Resistance (R)</label>
                    <Input
                      type="number"
                      step="any"
                      value={inputR}
                      onChange={(e) => setInputR(e.currentTarget.value)}
                      placeholder="50"
                      endEnhancer="Ω"
                      size={SIZE.compact}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1 font-serif">Reactance (X)</label>
                    <Input
                      type="number"
                      step="any"
                      value={inputX}
                      onChange={(e) => setInputX(e.currentTarget.value)}
                      placeholder="0"
                      endEnhancer="Ω"
                      size={SIZE.compact}
                    />
                  </div>
                </div>
                <Button type="submit" overrides={{ BaseButton: { style: { width: '100%' } } }}>
                  Plot Point
                </Button>
              </div>
            </Card>
          </form>

          {/* Calculated Values */}
          {point && (
            <motion.div
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-1 space-y-5">
                  <h4 className="font-serif font-medium text-lg text-gray-900 border-b pb-2">Calculated Parameters</h4>

                  <div className="space-y-4">
                    {/* Impedance */}
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1 font-serif">Impedance (Z)</div>
                      <div className="font-mono text-gray-900 text-lg">
                        {formatImpedance(point.resistance, point.reactance)}
                      </div>
                      <div className="font-mono text-gray-500 text-xs mt-0.5">
                        Normalized: {formatComplex(point.r, point.x)}
                      </div>
                    </div>

                    {/* Reflection Coefficient */}
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1 font-serif">Reflection Coefficient (Γ)</div>
                      <div className="font-mono text-gray-900 text-lg">
                        {point.gamma.toFixed(4)} ∠ {point.gammaAngle.toFixed(2)}°
                      </div>
                      <div className="font-mono text-gray-500 text-xs mt-0.5">
                        Rectangular: {formatComplex(point.gammaReal, point.gammaImag)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* VSWR */}
                      <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wider mb-1 font-serif">VSWR</div>
                        <div className="font-mono text-gray-900 text-lg">
                          {point.vswr === Infinity ? "∞" : point.vswr.toFixed(3)} : 1
                        </div>
                      </div>

                      {/* Return Loss */}
                      <div>
                        <div className="text-gray-500 text-xs uppercase tracking-wider mb-1 font-serif">Return Loss</div>
                        <div className="font-mono text-gray-900 text-lg">
                          {point.gamma > 0 ? (-20 * Math.log10(point.gamma)).toFixed(2) : "∞"} dB
                        </div>
                      </div>
                    </div>

                    {/* Mismatch Loss */}
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1 font-serif">Mismatch Loss</div>
                      <div className="font-mono text-gray-900 text-lg">
                        {(-10 * Math.log10(1 - point.gamma * point.gamma)).toFixed(3)} dB
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Quick Reference */}
          <div className="text-xs text-gray-500 space-y-1 px-1">
            <p className="font-serif font-bold">Key Points:</p>
            <ul className="list-disc list-inside space-y-0.5 font-serif">
              <li>Center (1,0): Matched load (Z = Z₀)</li>
              <li>Left edge (-1,0): Short circuit (Z = 0)</li>
              <li>Right edge (1,0): Open circuit (Z = ∞)</li>
              <li>Blue dashed circle: Constant VSWR</li>
            </ul>
          </div>
        </motion.div>
      </div>

      {/* Educational Content */}
      <motion.div
        initial={{ opacity: 0, filter: "blur(10px)" }}
        animate={{ opacity: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mt-8 prose max-w-none border-t border-gray-200 pt-8"
      >
        <h3 className="text-2xl font-serif font-medium text-gray-800 mb-4">Understanding the Smith Chart</h3>
        <p className="text-gray-700 leading-relaxed font-serif text-lg">
          The Smith Chart is a graphical tool used in RF and microwave engineering to visualize
          complex impedances and reflection coefficients. Every point on the chart represents
          a unique normalized impedance <em>z = Z/Z₀ = r + jx</em>.
        </p>

        <div className="grid md:grid-cols-2 gap-8 mt-8 not-prose">
          <Card>
            <div className="p-2">
              <h4 className="font-serif font-medium text-gray-900 mb-2 text-lg">Constant Resistance Circles</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Circles that pass through the right edge of the chart. Moving along a circle
                keeps resistance constant while reactance varies. These are crucial for impedance matching with series components.
              </p>
            </div>
          </Card>
          <Card>
            <div className="p-2">
              <h4 className="font-serif font-medium text-gray-900 mb-2 text-lg">Constant Reactance Arcs</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Arcs emanating from the right edge. Upper half represents inductive reactance (+jX),
                lower half represents capacitive reactance (-jX). Used for visualizing capacitive or inductive loads.
              </p>
            </div>
          </Card>
        </div>

        <div className="mt-8 p-6 bg-zinc-50 rounded-lg border border-zinc-200">
          <h4 className="font-serif font-medium text-zinc-900 mb-4 text-lg mt-0">Key Formulas</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 font-mono text-sm text-zinc-800">
            <div className="bg-white/60 p-2 rounded border border-zinc-200 text-center">Γ = (Z - Z₀) / (Z + Z₀)</div>
            <div className="bg-white/60 p-2 rounded border border-zinc-200 text-center">VSWR = (1 + |Γ|) / (1 - |Γ|)</div>
            <div className="bg-white/60 p-2 rounded border border-zinc-200 text-center">RL = -20 log₁₀|Γ| dB</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
